import React from 'react';
import { Box, Card, CardContent, Grid } from '@mui/material';
import upperFirst from 'lodash/upperFirst';
import { useTheme } from '@mui/material/styles';

import {
  CircularProgress,
  InfoField,
  LinearProgress,
  ProgressIndicator,
  Typography,
} from '@occc-app/design-system';
import { Unit_Of_Measure } from '@occc/graphql';

import { getRangeValue, getSurveyAwardDetails } from './utils';

import { titleByBasis, titleByUnitOfMeasure } from '../constants';
import { WalletData } from '../types';

type WalletDetailsProps = {
  walletData: WalletData;
};

export const WalletDetails: React.FC<WalletDetailsProps> = ({ walletData }) => {
  const theme = useTheme();
  const {
    wallet: loadedWallet,
    survey: loadedSurvey,
    productGroup: loadedProductGroup,
  } = walletData;

  const { data: wallet } = loadedWallet;
  const { data: survey } = loadedSurvey;
  const { data: productGroup } = loadedProductGroup;

  if (loadedWallet.error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (loadedWallet.loading && !wallet) {
    return <CircularProgress />;
  }

  if (!wallet) {
    return <Typography color='error'>No data. Something went wrong. Please try again.</Typography>;
  }

  const renderWalletSurveySection = () => {
    if (survey?.__typename !== 'WalletSteeringSurvey') {
      return;
    }

    const surveyConfiguration = survey?.configuration;
    const isNoSurveyDefined = !loadedSurvey.loading && !survey;

    const surveyAward = getSurveyAwardDetails(survey);

    const rangeValue =
      surveyConfiguration && wallet
        ? getRangeValue(
            surveyConfiguration?.minUnits ?? 0,
            surveyConfiguration?.maxUnits ?? 0,
            wallet.configuration.unitOfMeasure ?? Unit_Of_Measure.Unit,
          )
        : undefined;

    return (
      <Card sx={{ marginTop: 2 }}>
        {loadedSurvey.loading && <LinearProgress />}
        <CardContent>
          <Typography variant='h6'>Wallet Survey</Typography>

          {isNoSurveyDefined && (
            <Typography pt={3} color='error'>
              There is no survey associated with this wallet.
            </Typography>
          )}

          {survey && (
            <Grid container spacing={5} pt={3}>
              <Grid item xs={12} md={6}>
                <InfoField label='Survey Question' value={surveyConfiguration?.questionText} />
              </Grid>

              <Grid item xs={12} md={3}>
                <InfoField label='Range' value={rangeValue} />
              </Grid>

              <Grid item xs={12} md={3}>
                <InfoField label='Incentive' value={surveyAward || 'Not configured'} />
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
    );
  };

  const { walletCapacity, walletShare, walletOpportunity } = wallet.statistics;

  return (
    <Box display='flex' flexDirection='column'>
      <Card>
        {loadedWallet.loading && <LinearProgress />}

        <CardContent>
          <Typography variant='h6'>General Details</Typography>
          <Grid container spacing={5} pt={3}>
            <Grid item xs={12} md={3}>
              <InfoField label='Wallet Capacity' value={walletCapacity} />
            </Grid>

            <Grid item xs={12} md={3}>
              <InfoField label='Share of Wallet' value={walletShare} />
            </Grid>

            <Grid item xs={12} md={3}>
              <InfoField
                label='Wallet Opportunity'
                value={<Typography variant='subtitle2'>{walletOpportunity}</Typography>}
                disableTypography
              />
            </Grid>

            {walletCapacity > 0 && (
              <Grid item xs={12} md={3} display='flex' justifyContent='center' alignItems='center'>
                <ProgressIndicator
                  color={theme.palette.WalletSteeringOpportunityIndicatorColors[0]}
                  current={walletShare ?? 0}
                  total={walletCapacity}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ marginTop: 2 }}>
        {loadedWallet.loading && <LinearProgress />}
        <CardContent>
          <Typography variant='h6'>Wallet Configuration</Typography>

          <Grid container spacing={5} pt={3}>
            <Grid item xs={12} md={3}>
              <InfoField label='Product Group' value={productGroup?.name} />
            </Grid>

            {wallet.configuration.unitOfMeasure && (
              <Grid item xs={12} md={3}>
                <InfoField
                  label='Count Wallet by'
                  value={titleByUnitOfMeasure[wallet.configuration.unitOfMeasure]}
                />
              </Grid>
            )}

            {wallet.configuration.basis && (
              <Grid item xs={12} md={3}>
                <InfoField
                  label='Period'
                  value={upperFirst(titleByBasis[wallet.configuration.basis])}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {renderWalletSurveySection()}
    </Box>
  );
};
